/**
 * MCP-connection client (A-0008.07).
 *
 * Talks to bex-server's /cm/redis/mcp-connections routes. Per-protocol L1
 * entity (parallel to `opcode:`; one builder per protocol under AI Lab).
 *
 * Save flow for connections with bearer/api_key/oauth2 auth:
 *   (1) For each populated secret field, call writeMCPSecret(...) to persist
 *       the secret bytes in Vault and get back a JSON-pointer ref string.
 *   (2) Set `auth.<field>_ref` on the connection payload to that ref.
 *   (3) Call createMCPConnection(...) with the connection doc.
 */
import {
 bexHttpClient
} from '../../clients';
import {
    MCPConnectionEntity,
    CreateMCPConnectionRequest,
    UpdateMCPConnectionRequest,
    WriteMCPSecretRequest,
    WriteMCPSecretResponse,
    MCPSecretField,
} from '../bexService.types';

const MCP_SECRET_FIELDS: ReadonlyArray<MCPSecretField> = [
    'bearer_token',
    'api_key',
    'oauth2_client_secret',
];

class MCPConnectionService {
    private baseURL: string;

    constructor(baseURL = '/redis/mcp-connections') {
        this.baseURL = baseURL; // mounted at /cm/redis/mcp-connections (the bexHttpClient prepends /cm)
    }

    async createMCPConnection(data: CreateMCPConnectionRequest): Promise<MCPConnectionEntity> {
        const response = await bexHttpClient.post<MCPConnectionEntity>(`${this.baseURL}`, data);
        return response.data;
    }

    async updateMCPConnection(
        connectionId: string,
        data: UpdateMCPConnectionRequest,
    ): Promise<MCPConnectionEntity> {
        const response = await bexHttpClient.put<MCPConnectionEntity>(
            `${this.baseURL}/${connectionId}`,
            data,
        );
        return response.data;
    }

    async listMCPConnections(
        organizationId: string,
        subOrganizationId: string,
    ): Promise<MCPConnectionEntity[]> {
        try {
            const response = await bexHttpClient.get<MCPConnectionEntity[]>(
                `${this.baseURL}?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}`,
            );
            const data: any = response.data;
            if (Array.isArray(data)) return data;
            if (data?.status === 'success' && Array.isArray(data.data)) return data.data;
            return [];
        } catch (error) {
            console.error('Error fetching MCP connections:', error);
            throw error;
        }
    }

    async getMCPConnectionById(
        organizationId: string,
        subOrganizationId: string,
        connectionId: string,
    ): Promise<MCPConnectionEntity> {
        const response = await bexHttpClient.get<MCPConnectionEntity>(
            `${this.baseURL}/${connectionId}?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}`,
        );
        return response.data;
    }

    async deleteMCPConnection(
        organizationId: string,
        subOrganizationId: string,
        connectionId: string,
    ): Promise<{ message: string } | unknown> {
        const response = await bexHttpClient.delete(
            `${this.baseURL}/${connectionId}?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}`,
        );
        return response.data;
    }

    /**
     * Write an auth secret to Vault for an MCP connection. Returns the JSON-pointer
     * ref string to store under `auth.<field>_ref` on the connection doc.
     */
    async writeMCPSecret(
        connectionId: string,
        body: WriteMCPSecretRequest,
    ): Promise<WriteMCPSecretResponse> {
        if (!MCP_SECRET_FIELDS.includes(body.field)) {
            throw new Error(
                `MCP secret field '${body.field}' not allowed. Must be one of: ${MCP_SECRET_FIELDS.join(', ')}`,
            );
        }
        const response = await bexHttpClient.post<WriteMCPSecretResponse>(
            `${this.baseURL}/${connectionId}/secret`,
            body,
        );
        return response.data;
    }
}

export const mcpConnectionService = new MCPConnectionService();
export default mcpConnectionService;
